// src/pages/student-portal/achievements/AchievementsPage.js
// Student Portal - Achievements Page (purple theme)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    Chip,
    Skeleton,
    CircularProgress,
    Alert,
    Pagination,
    IconButton,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

const PER_PAGE = 12;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

/** Map common badge icon strings to MUI icon components */
const ICON_MAP = {
    trophy: EmojiEventsIcon,
    star: StarIcon,
    medal: MilitaryTechIcon,
    bolt: BoltIcon,
    fire: LocalFireDepartmentIcon,
    school: SchoolIcon,
    check: CheckCircleIcon,
    book: AutoStoriesIcon,
};

function resolveBadgeIcon(iconValue) {
    if (!iconValue) return EmojiEventsIcon;
    const key = String(iconValue).toLowerCase().trim();
    return ICON_MAP[key] || null;
}

function renderBadgeIcon(iconValue, sx = {}) {
    const MuiIcon = resolveBadgeIcon(iconValue);
    if (MuiIcon) {
        return <MuiIcon sx={{ fontSize: 48, color: 'white', ...sx }} />;
    }
    // Treat as emoji / text
    return (
        <Box component="span" sx={{ fontSize: '2.8rem', lineHeight: 1, ...sx }}>
            {iconValue}
        </Box>
    );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, title, value, color, loading }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: color,
                boxShadow: `0 4px 20px ${color}20`,
                transform: 'translateY(-2px)',
            },
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.82rem' }}
                    >
                        {title}
                    </Typography>
                    {loading ? (
                        <Skeleton variant="text" width={48} sx={{ fontSize: '1.6rem' }} />
                    ) : (
                        <Typography
                            variant="h5"
                            sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}
                        >
                            {value}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Badge Type Button ────────────────────────────────────────────────────────

const BadgeTypeButton = ({ badgeType, isActive, onClick }) => {
    const name = badgeType.badge_name || badgeType.name || badgeType.badge_type || badgeType.type || 'Badge';
    const icon = badgeType.badge_icon || badgeType.icon;
    const color = badgeType.badge_color || badgeType.color || ACCENT;
    const earnedCount = badgeType.total_awarded ?? badgeType.earned_count ?? 0;
    const hasEarned = earnedCount > 0;

    return (
        <Button
            onClick={onClick}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                p: 1.5,
                borderRadius: '14px',
                border: isActive ? `2px solid ${color}` : '2px solid transparent',
                bgcolor: isActive ? `${color}12` : 'white',
                boxShadow: isActive ? `0 4px 16px ${color}25` : '0 1px 4px rgba(0,0,0,0.06)',
                opacity: hasEarned ? 1 : 0.5,
                minWidth: 90,
                textTransform: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: `${color}10`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${color}20`,
                    opacity: 1,
                },
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: `${color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {renderBadgeIcon(icon, { fontSize: 26, color })}
            </Box>
            <Typography
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    color: '#1e293b',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    maxWidth: 80,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {name}
            </Typography>
            <Chip
                label={earnedCount}
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    height: 20,
                    minWidth: 28,
                    bgcolor: hasEarned ? `${color}18` : '#f1f5f9',
                    color: hasEarned ? color : '#94a3b8',
                }}
            />
        </Button>
    );
};

// ─── Achievement Card ─────────────────────────────────────────────────────────

const AchievementCard = ({ achievement }) => {
    const badgeName = achievement.badge_name || 'Badge';
    const badgeDescription = achievement.badge_description || '';
    const badgeIcon = achievement.badge_icon;
    const badgeColor = achievement.badge_color || ACCENT;
    const points = achievement.points_awarded || 0;
    const earnedAt = achievement.earned_at;
    const courseTitle = achievement.course?.title;

    return (
        <Card
            sx={{
                height: '100%',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                    borderColor: badgeColor,
                },
            }}
        >
            {/* Colored header with decorative circles and badge icon */}
            <Box
                sx={{
                    position: 'relative',
                    background: `linear-gradient(135deg, ${badgeColor} 0%, ${badgeColor}cc 100%)`,
                    py: 3.5,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.12)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -15,
                        left: -15,
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.08)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: 20,
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.06)',
                    }}
                />

                {/* Large badge icon */}
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {renderBadgeIcon(badgeIcon, { fontSize: 40, color: 'white' })}
                </Box>

                {/* Badge name */}
                <Typography
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: 'white',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.3,
                    }}
                >
                    {badgeName}
                </Typography>

                {/* Points badge */}
                {points > 0 && (
                    <Chip
                        icon={<StarIcon sx={{ fontSize: 14, color: '#f59e0b !important' }} />}
                        label={`${points} pts`}
                        size="small"
                        sx={{
                            mt: 1,
                            fontFamily,
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: 'rgba(255,255,255,0.92)',
                            color: '#1e293b',
                            height: 26,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    />
                )}
            </Box>

            {/* Card body */}
            <CardContent
                sx={{
                    p: 2.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Description */}
                {badgeDescription && (
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily,
                            color: '#64748b',
                            fontSize: '0.82rem',
                            lineHeight: 1.6,
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            flex: 1,
                        }}
                    >
                        {badgeDescription}
                    </Typography>
                )}

                {/* Meta info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 'auto' }}>
                    {earnedAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography
                                variant="caption"
                                sx={{ fontFamily, color: '#64748b', fontWeight: 500 }}
                            >
                                Earned {formatDate(earnedAt)}
                            </Typography>
                        </Box>
                    )}
                    {courseTitle && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoStoriesIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontFamily,
                                    color: '#64748b',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {courseTitle}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// ─── Achievement Card Skeleton ────────────────────────────────────────────────

const AchievementCardSkeleton = () => (
    <Card
        sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            overflow: 'hidden',
        }}
    >
        <Skeleton variant="rectangular" height={180} />
        <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="text" width="90%" sx={{ fontSize: '0.85rem', mb: 0.5 }} />
            <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem', mb: 1.5 }} />
            <Skeleton variant="text" width="50%" sx={{ fontSize: '0.75rem' }} />
            <Skeleton variant="text" width="60%" sx={{ fontSize: '0.75rem' }} />
        </CardContent>
    </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AchievementsPage() {
    const navigate = useNavigate();

    // Data state
    const [achievements, setAchievements] = useState([]);
    const [badgeTypes, setBadgeTypes] = useState([]);
    const [summary, setSummary] = useState(null);

    // Loading state
    const [loadingAchievements, setLoadingAchievements] = useState(true);
    const [loadingTypes, setLoadingTypes] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);

    // Error
    const [error, setError] = useState('');

    // Pagination & filter
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeFilter, setActiveFilter] = useState('');
    const [filtersExpanded, setFiltersExpanded] = useState(true);

    // ── Fetch Summary ─────────────────────────────────────────────────────────

    const fetchSummary = useCallback(async () => {
        setLoadingSummary(true);
        try {
            const res = await axiosInstance.get('/api/lms/portal/achievements/summary');
            setSummary(res.data);
        } catch (err) {
            console.error('Failed to fetch achievements summary:', err);
        } finally {
            setLoadingSummary(false);
        }
    }, []);

    // ── Fetch Badge Types ─────────────────────────────────────────────────────

    const fetchBadgeTypes = useCallback(async () => {
        setLoadingTypes(true);
        try {
            const res = await axiosInstance.get('/api/lms/portal/achievements/types');
            const data = res.data;
            setBadgeTypes(data.badge_types || data.types || data.data || []);
        } catch (err) {
            console.error('Failed to fetch badge types:', err);
        } finally {
            setLoadingTypes(false);
        }
    }, []);

    // ── Fetch Achievements ────────────────────────────────────────────────────

    const fetchAchievements = useCallback(async (pg, badgeType) => {
        setLoadingAchievements(true);
        setError('');
        try {
            const params = { page: pg, per_page: PER_PAGE };
            if (badgeType) params.badge_type = badgeType;
            const res = await axiosInstance.get('/api/lms/portal/achievements', { params });
            const data = res.data;
            setAchievements(data.achievements || data.data || data.results || []);
            const total = data.total_pages || data.pages || Math.ceil((data.total || 0) / PER_PAGE) || 1;
            setTotalPages(total);
        } catch (err) {
            console.error('Failed to fetch achievements:', err);
            setError(err.response?.data?.message || 'Failed to load achievements.');
        } finally {
            setLoadingAchievements(false);
        }
    }, []);

    // ── Initial load ──────────────────────────────────────────────────────────

    useEffect(() => {
        fetchSummary();
        fetchBadgeTypes();
    }, [fetchSummary, fetchBadgeTypes]);

    useEffect(() => {
        fetchAchievements(page, activeFilter);
    }, [fetchAchievements, page, activeFilter]);

    // ── Event handlers ────────────────────────────────────────────────────────

    const handleFilterClick = (badgeType) => {
        const typeKey = badgeType.badge_type || badgeType.type || '';
        if (activeFilter === typeKey) {
            // Toggle off
            setActiveFilter('');
        } else {
            setActiveFilter(typeKey);
        }
        setPage(1);
    };

    const handleClearFilter = () => {
        setActiveFilter('');
        setPage(1);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ── Derived values ────────────────────────────────────────────────────────

    const totalBadges = summary?.total_badges ?? '--';
    const coursesCompleted = summary?.badge_types?.courses_completed ?? summary?.courses_completed ?? '--';
    const perfectScores = summary?.badge_types?.perfect_scores ?? summary?.perfect_scores ?? '--';
    const totalPoints = summary?.total_points_from_badges ?? summary?.total_points ?? '--';
    const isLoadingStats = loadingSummary;

    const activeFilterName = activeFilter
        ? badgeTypes.find(
              (bt) => (bt.badge_type || bt.type) === activeFilter
          )
        : null;
    const activeFilterLabel = activeFilterName
        ? activeFilterName.badge_name || activeFilterName.name || activeFilter
        : '';

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    background: `linear-gradient(135deg, ${ACCENT} 0%, #7c3aed 100%)`,
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmojiEventsIcon sx={{ fontSize: 40, color: 'white' }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily,
                                fontWeight: 800,
                                color: 'white',
                                fontSize: { xs: '1.5rem', md: '2rem' },
                            }}
                        >
                            My Achievements
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily,
                                color: 'rgba(255,255,255,0.85)',
                                fontSize: '0.95rem',
                            }}
                        >
                            Track your badges, accomplishments, and learning milestones
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* ── Stats Grid (4 cards) ────────────────────────────────── */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            icon={MilitaryTechIcon}
                            title="Total Badges"
                            value={totalBadges}
                            color="#f59e0b"
                            loading={isLoadingStats}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            icon={SchoolIcon}
                            title="Courses Completed"
                            value={coursesCompleted}
                            color="#10b981"
                            loading={isLoadingStats}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            icon={LocalFireDepartmentIcon}
                            title="Perfect Scores"
                            value={perfectScores}
                            color="#f97316"
                            loading={isLoadingStats}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatCard
                            icon={StarIcon}
                            title="Badge Points"
                            value={
                                typeof totalPoints === 'number'
                                    ? totalPoints.toLocaleString()
                                    : totalPoints
                            }
                            color={ACCENT}
                            loading={isLoadingStats}
                        />
                    </Grid>
                </Grid>

                {/* ── Badge Collection Section ────────────────────────────── */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        p: 2.5,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: filtersExpanded ? 2 : 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmojiEventsIcon sx={{ color: ACCENT, fontSize: 22 }} />
                            <Typography
                                sx={{
                                    fontFamily,
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontSize: '1.05rem',
                                }}
                            >
                                Badge Collection
                            </Typography>
                            {!loadingTypes && (
                                <Chip
                                    label={`${badgeTypes.length} types`}
                                    size="small"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        height: 22,
                                        bgcolor: `${ACCENT}12`,
                                        color: ACCENT,
                                    }}
                                />
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            sx={{ color: '#64748b' }}
                        >
                            <TuneIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>

                    {filtersExpanded && (
                        <>
                            {loadingTypes ? (
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rounded"
                                            width={90}
                                            height={100}
                                            sx={{ borderRadius: '14px' }}
                                        />
                                    ))}
                                </Box>
                            ) : badgeTypes.length === 0 ? (
                                <Typography
                                    sx={{
                                        fontFamily,
                                        color: '#94a3b8',
                                        fontSize: '0.85rem',
                                        textAlign: 'center',
                                        py: 2,
                                    }}
                                >
                                    No badge types available yet
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {badgeTypes.map((bt, idx) => {
                                        const typeKey = bt.badge_type || bt.type || idx;
                                        return (
                                            <BadgeTypeButton
                                                key={typeKey}
                                                badgeType={bt}
                                                isActive={activeFilter === typeKey}
                                                onClick={() => handleFilterClick(bt)}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        </>
                    )}
                </Paper>

                {/* ── Filter Controls Bar ─────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2.5,
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FilterListIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                        <Typography
                            sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}
                        >
                            {activeFilter ? (
                                <>
                                    Showing:{' '}
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 700, color: '#1e293b' }}
                                    >
                                        {activeFilterLabel}
                                    </Box>
                                </>
                            ) : (
                                'Showing all achievements'
                            )}
                        </Typography>
                        {activeFilter && (
                            <Button
                                size="small"
                                onClick={handleClearFilter}
                                sx={{
                                    fontFamily,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.78rem',
                                    color: ACCENT,
                                    '&:hover': { bgcolor: `${ACCENT}10` },
                                }}
                            >
                                Clear filter
                            </Button>
                        )}
                    </Box>
                    {loadingAchievements && (
                        <CircularProgress size={18} sx={{ color: ACCENT }} />
                    )}
                </Box>

                {/* ── Error ───────────────────────────────────────────────── */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => fetchAchievements(page, activeFilter)}
                                sx={{ fontFamily, textTransform: 'none' }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ── Achievement Cards Grid ──────────────────────────────── */}
                {loadingAchievements ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <AchievementCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : achievements.length === 0 ? (
                    /* ── Empty State ──────────────────────────────────────── */
                    <Paper
                        elevation={0}
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                        }}
                    >
                        {activeFilter ? (
                            <>
                                <FilterListIcon
                                    sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        color: '#1e293b',
                                        mb: 1,
                                    }}
                                >
                                    No achievements found for this filter
                                </Typography>
                                <Typography
                                    sx={{ fontFamily, color: '#64748b', mb: 3, fontSize: '0.92rem' }}
                                >
                                    Try selecting a different badge type or clear the filter to see all achievements.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={handleClearFilter}
                                    sx={{
                                        fontFamily,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: ACCENT,
                                        color: ACCENT,
                                        borderRadius: '10px',
                                        px: 3,
                                        '&:hover': {
                                            borderColor: ACCENT,
                                            bgcolor: `${ACCENT}08`,
                                        },
                                    }}
                                >
                                    Clear Filter
                                </Button>
                            </>
                        ) : (
                            <>
                                <EmojiEventsIcon
                                    sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        color: '#1e293b',
                                        mb: 1,
                                    }}
                                >
                                    No badges earned yet
                                </Typography>
                                <Typography
                                    sx={{ fontFamily, color: '#64748b', mb: 3, fontSize: '0.92rem' }}
                                >
                                    Complete courses and activities to earn badges and achievements!
                                </Typography>
                            </>
                        )}
                    </Paper>
                ) : (
                    <>
                        <Grid container spacing={3}>
                            {achievements.map((achievement) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={achievement.id}
                                >
                                    <AchievementCard achievement={achievement} />
                                </Grid>
                            ))}
                        </Grid>

                        {/* ── Pagination ──────────────────────────────────── */}
                        {totalPages > 1 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 4,
                                }}
                            >
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    shape="rounded"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontFamily,
                                            fontWeight: 600,
                                            borderRadius: '10px',
                                            '&.Mui-selected': {
                                                bgcolor: ACCENT,
                                                color: 'white',
                                                '&:hover': {
                                                    bgcolor: '#7c3aed',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
